import { useRef, useState } from "react";

import { api, MAX_UPLOAD_BYTES } from "@/lib/api";
import { isSupportedDocumentFile } from "@/lib/documentTypes";
import { formatFileSize } from "@/lib/utils";

// NOTE: BodySizeLimitMiddleware disconnects (no HTTP 413), so files that bypass the
// MAX_UPLOAD_BYTES guard surface as network errors and are retried once before failing.

/** Detects browser-level network failures (no HTTP response received).
 * Chrome: "Failed to fetch", Firefox: "NetworkError…", Safari: "Load failed", abort: "aborted". */
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError && /failed to fetch|networkerror|load failed|aborted/i.test(err.message);
}

/**
 * Retries `fn` once (default) on network-level failures only.
 * Network TypeErrors arise when BodySizeLimitMiddleware (app/main.py) disconnects
 * oversized requests instead of returning HTTP 413 — making them indistinguishable
 * from transient connectivity drops. HTTP 4xx/5xx responses propagate immediately.
 */
async function withRetry<T>(fn: () => Promise<T>, opts?: { retries?: number; delayMs?: number; onRetry?: () => void }): Promise<T> {
  const { retries = 1, delayMs = 2000, onRetry } = opts ?? {};
  try {
    return await fn();
  } catch (err) {
    if (retries > 0 && isNetworkError(err)) {
      onRetry?.();
      await new Promise((r) => setTimeout(r, delayMs));
      return withRetry(fn, { retries: retries - 1, delayMs, onRetry });
    }
    throw err;
  }
}

interface WalkedFile {
  relativePath: string;
  file: File;
}

// The File and Directory Entries API returns at most 100 entries per readEntries() call; repeat until empty.
async function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  const all: FileSystemEntry[] = [];
  let batch: FileSystemEntry[] = [];
  do {
    batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    all.push(...batch);
  } while (batch.length > 0);
  return all;
}

const WALK_LIMITS = { maxFiles: 200, maxDirs: 100, maxDepth: 10 };

async function walkEntry(entry: FileSystemEntry, basePath: string): Promise<{ files: WalkedFile[]; dirs: string[] }> {
  const files: WalkedFile[] = [];
  const dirs: string[] = [];

  async function recurse(current: FileSystemEntry, path: string, depth: number) {
    if (depth > WALK_LIMITS.maxDepth) return;
    if (current.isFile) {
      if (files.length >= WALK_LIMITS.maxFiles) return;
      const file = await new Promise<File>((resolve, reject) => (current as FileSystemFileEntry).file(resolve, reject));
      files.push({ relativePath: path + file.name, file });
    } else if (current.isDirectory) {
      if (dirs.length >= WALK_LIMITS.maxDirs) return;
      const dirPath = path + current.name + "/";
      dirs.push(dirPath);
      const reader = (current as FileSystemDirectoryEntry).createReader();
      const children = await readAllEntries(reader);
      for (const child of children) {
        await recurse(child, dirPath, depth + 1);
      }
    }
  }

  await recurse(entry, basePath, 0);
  return { files, dirs };
}

/** Reads the internal drag payload set by ProjectFileTree's dragstart handlers.
 * Expected shape: `{ type: "document" | "folder", id: string }`. Returns null
 * for external (OS file) drags or malformed JSON. */
function dragPayload(event: React.DragEvent) {
  const raw = event.dataTransfer.getData("application/json");
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as { type?: string; id?: string };
  } catch {
    return null;
  }
}

interface UseProjectFileUploadOptions {
  projectId: string;
  onRefresh: () => Promise<void>;
}

/**
 * Owns the upload / drag-and-drop concern for the project file tree:
 * file uploads with size-limit validation, OS-directory drops (folder
 * hierarchy recreation), internal document/folder move drops, and the
 * busy/error/drag-over state slices those flows drive.
 *
 * `setBusy` and `setError` are intentionally exposed so the consuming
 * component's sibling mutations (rename, delete, folder CRUD) can drive the
 * same shared status banner without duplicating state.
 */
export function useProjectFileUpload({ projectId, onRefresh }: UseProjectFileUploadOptions) {
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dragCounter = useRef(0);

  const uploadFiles = async (files: FileList | File[], folderId: string | null) => {
    const fileArr = Array.from(files);
    const supportedFiles = fileArr.filter((f) => isSupportedDocumentFile(f.name));
    const unsupported = fileArr.filter((f) => !isSupportedDocumentFile(f.name));
    const uploadable = supportedFiles.filter((f) => f.size <= MAX_UPLOAD_BYTES);
    const oversized = supportedFiles.filter((f) => f.size > MAX_UPLOAD_BYTES);
    setError(null);
    const failures: string[] = [];
    for (const f of unsupported) {
      failures.push(`${f.name} \u2014 only PDF, DOCX, and DOC files are supported`);
    }
    for (const f of oversized) {
      failures.push(`${f.name} (${formatFileSize(f.size)}) exceeds ${formatFileSize(MAX_UPLOAD_BYTES)} limit`);
    }
    // Only enter the busy state when something will actually be uploaded;
    // pure-validation failures should report without an "Uploading\u2026" flash.
    if (uploadable.length > 0) {
      setBusy(true);
      setBusyLabel("Uploading\u2026");
    }
    try {
      let count = 0;
      for (const file of uploadable) {
        count++;
        if (uploadable.length > 1) setBusyLabel(`Uploading ${count}/${uploadable.length}`);
        try {
          const uploaded = await withRetry(
            () => api.projects.uploadDocument(projectId, file),
            { onRetry: () => setBusyLabel(`Retrying ${file.name}\u2026`) }
          );
          if (folderId) {
            try {
              await api.projects.moveDocumentToFolder(projectId, uploaded.id, folderId);
            } catch (moveError) {
              failures.push(`${file.name} uploaded but could not be moved to folder \u2014 ${moveError instanceof Error ? moveError.message : "move failed"}`);
            }
          }
          // Progressive tree update mid-batch. Skip for the last file — the
          // final awaited refresh below handles it exclusively to avoid a
          // cancellation race with TanStack Query's refetch deduplication.
          if (count < uploadable.length) {
            onRefresh().catch(() => {});
          }
        } catch (fileError) {
          const reason = isNetworkError(fileError)
            ? `network error uploading ${file.name} (${formatFileSize(file.size)}) \u2014 file may be too large or connection was interrupted`
            : fileError instanceof Error ? fileError.message : "unknown error";
          failures.push(reason);
        }
      }
      // Authoritative final refresh — for single-file uploads or the last file
      // in a batch, this is the sole refresh call (no dedup/cancellation race).
      if (uploadable.length > 0) {
        try { await onRefresh(); } catch { /* best-effort */ }
      }
    } catch (uploadError) {
      failures.push(uploadError instanceof Error ? uploadError.message : "Upload failed.");
    } finally {
      setBusy(false);
      setBusyLabel(null);
      if (failures.length) {
        setError(failures.length === 1 ? failures[0] : `${failures.length} file(s) failed: ${failures.join("; ")}`);
      }
    }
  };

  const handleDirectoryDrop = async (entries: FileSystemEntry[], folderId: string | null) => {
    setBusy(true);
    setBusyLabel("Scanning folder\u2026");
    setError(null);
    const failures: string[] = [];
    try {
      const allFiles: WalkedFile[] = [];
      const allDirs: string[] = [];

      for (const entry of entries) {
        const { files, dirs } = await walkEntry(entry, "");
        allFiles.push(...files);
        allDirs.push(...dirs);
      }

      const supportedFiles = allFiles.filter((f) => isSupportedDocumentFile(f.file.name));
      const skippedCount = allFiles.length - supportedFiles.length;

      if (!supportedFiles.length) {
        setError(skippedCount > 0 ? `${skippedCount} file(s) skipped \u2014 only PDF, DOCX, and DOC files are supported` : "No files found in dropped folder.");
        return;
      }

      if (supportedFiles.length > WALK_LIMITS.maxFiles || allDirs.length > WALK_LIMITS.maxDirs) {
        setError(`Drop is too large (${supportedFiles.length} files, ${allDirs.length} folders). Max ${WALK_LIMITS.maxFiles} files and ${WALK_LIMITS.maxDirs} folders per drop.`);
        return;
      }

      // Reject files over the size limit before starting any uploads
      const oversized = supportedFiles.filter((f) => f.file.size > MAX_UPLOAD_BYTES);
      const uploadable = supportedFiles.filter((f) => f.file.size <= MAX_UPLOAD_BYTES);
      for (const f of oversized) {
        failures.push(`${f.file.name} (${formatFileSize(f.file.size)}) exceeds ${formatFileSize(MAX_UPLOAD_BYTES)} limit`);
      }

      if (!uploadable.length) {
        return; // failures[] populated above; finally block will call setError
      }

      // Sort dirs by depth so parents are created before children
      allDirs.sort((a, b) => a.split("/").length - b.split("/").length);

      // Create folder hierarchy
      const folderMap = new Map<string, string>(); // dirPath -> folderId
      const failedDirPaths = new Set<string>();
      for (const dirPath of allDirs) {
        const parts = dirPath.replace(/\/$/, "").split("/");
        const name = parts[parts.length - 1];
        const parentPath = parts.length > 1 ? parts.slice(0, -1).join("/") + "/" : null;
        if (parentPath && failedDirPaths.has(parentPath)) {
          failedDirPaths.add(dirPath);
          continue;
        }
        const parentId = parentPath ? (folderMap.get(parentPath) ?? folderId) : folderId;
        setBusyLabel(`Creating folder: ${name}`);
        try {
          const created = await withRetry(
            () => api.projects.createFolder(projectId, { name, parent_folder_id: parentId }),
            { onRetry: () => setBusyLabel(`Retrying folder: ${name}\u2026`) }
          );
          folderMap.set(dirPath, created.id);
        } catch (folderError) {
          failedDirPaths.add(dirPath);
          const reason = isNetworkError(folderError)
            ? `network error creating folder "${name}"`
            : folderError instanceof Error ? folderError.message : "folder creation failed";
          failures.push(reason);
        }
      }

      // Upload files into their respective folders — pre-compute attemptable
      // count (excludes files whose target folder failed to create) so the
      // last-file guard and progress label use the correct denominator.
      const attemptableCount = uploadable.filter(({ relativePath }) => {
        const dir = relativePath.includes("/") ? relativePath.slice(0, relativePath.lastIndexOf("/") + 1) : null;
        return !dir || !failedDirPaths.has(dir);
      }).length;
      let uploaded = 0;
      for (const { relativePath, file } of uploadable) {
        const fileDirPath = relativePath.includes("/") ? relativePath.slice(0, relativePath.lastIndexOf("/") + 1) : null;
        if (fileDirPath && failedDirPaths.has(fileDirPath)) {
          failures.push(`${file.name} skipped \u2014 its folder could not be created`);
          continue;
        }
        uploaded++;
        setBusyLabel(`Uploading ${uploaded}/${attemptableCount}`);
        const targetFolderId = fileDirPath ? (folderMap.get(fileDirPath) ?? folderId) : folderId;
        try {
          const doc = await withRetry(
            () => api.projects.uploadDocument(projectId, file),
            { onRetry: () => setBusyLabel(`Retrying ${file.name}\u2026`) }
          );
          if (targetFolderId) {
            try {
              await api.projects.moveDocumentToFolder(projectId, doc.id, targetFolderId);
            } catch (moveError) {
              failures.push(`${file.name} uploaded but could not be moved to folder \u2014 ${moveError instanceof Error ? moveError.message : "move failed"}`);
            }
          }
          // Progressive tree update mid-batch. Skip for the last file — the
          // final awaited refresh below handles it exclusively to avoid a
          // cancellation race with TanStack Query's refetch deduplication.
          if (uploaded < attemptableCount) {
            onRefresh().catch(() => {});
          }
        } catch (fileError) {
          const reason = isNetworkError(fileError)
            ? `network error uploading ${file.name} (${formatFileSize(file.size)}) \u2014 file may be too large or connection was interrupted`
            : fileError instanceof Error ? fileError.message : "unknown error";
          failures.push(reason);
        }
      }

      if (skippedCount > 0) {
        failures.push(`${skippedCount} file(s) skipped \u2014 only PDF, DOCX, and DOC files are supported`);
      }
      // Authoritative final refresh — for the last file this is the sole refresh
      // call (no dedup/cancellation race).
      if (uploaded > 0) {
        try { await onRefresh(); } catch { /* best-effort */ }
      }
    } catch (uploadError) {
      const msg = uploadError instanceof Error ? uploadError.message : "Directory upload failed.";
      failures.push(msg);
    } finally {
      setBusy(false);
      setBusyLabel(null);
      if (failures.length) {
        setError(failures.length === 1 ? failures[0] : `${failures.length} issue(s): ${failures.join("; ")}`);
      }
    }
  };

  const handleDrop = async (event: React.DragEvent, folderId: string | null) => {
    event.preventDefault();
    dragCounter.current = 0;
    setDragOver(false);

    // Capture entries synchronously before any await
    const items = event.dataTransfer.items;
    const entries: FileSystemEntry[] = [];
    if (items?.length) {
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }
    }

    // Check if any entry is a directory
    const hasDirectory = entries.some((e) => e.isDirectory);
    if (hasDirectory) {
      await handleDirectoryDrop(entries, folderId);
      return;
    }

    if (event.dataTransfer.files.length) {
      await uploadFiles(event.dataTransfer.files, folderId);
      return;
    }

    const payload = dragPayload(event);
    if (!payload?.id) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (payload.type === "document") {
        await api.projects.moveDocumentToFolder(projectId, payload.id, folderId);
      } else if (payload.type === "folder") {
        await api.projects.updateFolder(projectId, payload.id, { parent_folder_id: folderId });
      }
      await onRefresh();
    } catch (dropError) {
      setError(dropError instanceof Error ? dropError.message : "Move failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleSectionDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    dragCounter.current++;
    const hasExternalFiles = event.dataTransfer.types.includes("Files");
    if (dragCounter.current === 1 && hasExternalFiles) setDragOver(true);
  };

  const handleSectionDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragOver(false);
  };

  const handleSectionDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  return {
    busy,
    setBusy,
    busyLabel,
    error,
    setError,
    dragOver,
    uploadFiles,
    handleDrop,
    handleSectionDragEnter,
    handleSectionDragLeave,
    handleSectionDragOver
  };
}
